import React, { useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Box, Divider, Typography } from "@mui/material";

import { BrandContext } from "../../App";
import ProgramInfoCard from "../../components/complex/ProgramInfoCard";
import ProgramTopicsDropdown from "../../components/complex/ProgramTopicsDropdowns";
import { useTheme } from "@mui/material/styles";
import DefaultButton from "../../components/simple/DefaultButton";

export default function DashboardProgram() {
  const theme = useTheme();
  const { brand, user, trainingProgram, authToken, isMobile } =
    React.useContext(BrandContext);
  const [expandedStates, setExpandedStates] = useState({});
  const [progress, setProgress] = React.useState(0);
  const [totalSteps, setTotalSteps] = React.useState(0);
  const [allLessons, setAllLessons] = React.useState(0);
  const [completedSteps, setCompletedSteps] = React.useState(0);
  const [allOpen, setAllOpen] = React.useState(false);

  function updateTrainingPrograms() {
    let totalLessonCount = 0;

    trainingProgram?.chapters.forEach((chapter, index) => {
      if (chapter && chapter.scenarios) {
        const totalLessons = chapter.scenarios.length;
        const completedLessons = chapter.scenarios.filter(
          (scenario) => scenario.completed === true
        ).length;
        totalLessonCount += totalLessons;

        chapter.completed =
          totalLessons > 0 && completedLessons === totalLessons;

        if (user && user.is_tester) {
          chapter.locked = false;
        } else if (index > 0) {
          const prevChapterCompleted =
            trainingProgram.chapters[index - 1].completed;
          chapter.locked = !prevChapterCompleted;
        } else {
          chapter.locked = false;
        }

        chapter.scenarios.forEach((scenario, lessonIndex) => {
          if (chapter.locked) {
            scenario.locked = true;
          } else {
            if (user && user.is_tester) {
              scenario.locked = false;
            } else if (lessonIndex === 0) {
              scenario.locked = false;
            } else {
              const prevScenario = chapter.scenarios[lessonIndex - 1];
              scenario.locked = !prevScenario.completed;
            }
          }
        });
      }
    });

    const totalSteps = trainingProgram.chapters.length;
    const completedSteps = trainingProgram.chapters.filter(
      (chapter) => chapter.completed === true
    ).length;

    const progress = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

    for (const chapter of trainingProgram.chapters) {
      if (chapter && chapter.scenarios) {
        const firstIncomplete = chapter.scenarios.find((s) => !s.completed);
        if (firstIncomplete) {
          setExpandedStates((prev) => ({ ...prev, [chapter.id]: true }));
          break;
        }
      }
    }

    setAllLessons(totalLessonCount);
    setTotalSteps(totalSteps);
    setCompletedSteps(completedSteps);
    setProgress(progress);
  }

  const handleExpandAll = () => {
    if (!trainingProgram?.chapters) return;

    const chapters = trainingProgram.chapters.map((chapter) => chapter.id);
    const areAllOpen = chapters.every((id) => expandedStates[id]);

    const newExpandedStates = {};
    chapters.forEach((id) => {
      newExpandedStates[id] = !areAllOpen;
    });

    setAllOpen(!areAllOpen);
    setExpandedStates(newExpandedStates);
  };

  React.useEffect(() => {
    if (trainingProgram) {
      updateTrainingPrograms();
    }
  }, [user, authToken, trainingProgram]);

  if (!trainingProgram) {
    return (
      <Box sx={{ padding: theme.containers.paddingDesktop }}>
        <Typography variant="h6">Loading your training program...</Typography>
        <Typography variant="body2" color="text.secondary">
          If this takes more than a few seconds, try refreshing the page.
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      {trainingProgram != null && (
        <>
          <ProgramInfoCard
            trainingProgram={trainingProgram}
            progress={progress}
            totalSteps={totalSteps}
            allLessons={allLessons}
            completedSteps={completedSteps}
            isMobile={isMobile}
          />
          <Box
            id="training-program-topics-container"
            sx={{
              p: isMobile ? "1.2rem" : theme.containers.paddingDesktop,
              border: `1px solid ${theme.palette.border.tertiary}`,
              borderRadius: theme.shape.radius.md,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  textTransform: "uppercase",
                  fontSize: isMobile ? "12px" : "inherit",
                  textAlign: isMobile ? "center" : "left",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                Program Topics
              </Typography>
              <DefaultButton
                backgroundColor={"transparent"}
                buttonSize="sm"
                textColor={theme.palette.text.primary}
                borderColor={theme.palette.border.brand}
                onClick={handleExpandAll}
                sx={{ display: isMobile ? "none" : "block" }}
              >
                {allOpen ? "Minimise all Sections" : "Expand all sections"}
              </DefaultButton>
            </Box>
            {trainingProgram.chapters &&
              trainingProgram.chapters.length > 0 &&
              trainingProgram.chapters.map((chapter, index, array) => {
                // Calculate whether all scenarios are completed
                const allScenariosCompleted =
                  chapter.scenarios &&
                  chapter.scenarios.every(
                    (scenario) => scenario.completed === true
                  );

                // Calculate the percentage of completed scenarios
                const totalScenarios = chapter.scenarios?.length || 0;
                const completedScenarios =
                  chapter.scenarios?.filter(
                    (scenario) => scenario.completed === true
                  ).length || 0;
                const percentageCompleted =
                  totalScenarios > 0
                    ? Math.round((completedScenarios / totalScenarios) * 100)
                    : 0;
                return (
                  <React.Fragment key={chapter.id}>
                    <ProgramTopicsDropdown
                      chapter={chapter}
                      allScenariosCompleted={allScenariosCompleted}
                      percentageCompleted={percentageCompleted}
                      expandedStates={expandedStates}
                      setExpandedStates={setExpandedStates}
                      setAllOpen={setAllOpen}
                      allChapterIds={trainingProgram.chapters.map(
                        (chapter) => chapter.id
                      )}
                      locked={chapter.locked}
                      isMobile={isMobile}
                    />
                    {index !== array.length - 1 && (
                      <Divider
                        sx={{
                          backgroundColor: theme.palette.border.tertiary,
                          height: 2,
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
          </Box>
        </>
      )}
    </React.Fragment>
  );
}
